import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Delete,
  Visibility,
  Science,
} from '@mui/icons-material';
import { TrainingSample, ReviewStatus, LLMAnalysis } from '../../types';
import { formatSmartDateTime } from '../../utils/dateUtils';

interface TrainingSampleCardProps {
  sample: TrainingSample;
  signedImageResult?: {
    loading: boolean;
    signedUrl?: string;
    error?: string;
  } | null;
  onView: (sample: TrainingSample) => void;
  onDelete: (sample: TrainingSample, event: React.MouseEvent) => void;
  onRunInference: (sample: TrainingSample, event: React.MouseEvent) => void;
}

export const TrainingSampleCard: React.FC<TrainingSampleCardProps> = ({
  sample,
  signedImageResult,
  onView,
  onDelete,
  onRunInference,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const llmAnalysis = JSON.parse(JSON.parse(sample.llm_analysis));

  const getStatusColor = (status: ReviewStatus) => {
    switch (status) {
      case ReviewStatus.PENDING:
        return 'warning';
      case ReviewStatus.IN_REVIEW:
        return 'info';
      case ReviewStatus.REVIEWED:
        return 'primary';
      case ReviewStatus.APPROVED:
        return 'success';
      case ReviewStatus.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  const renderImage = () => {
    if (signedImageResult?.loading) {
      return (
        <Box 
          sx={{ 
            width: '100%',
            height: isMobile ? 200 : '100%',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (signedImageResult?.signedUrl) {
      return (
        <CardMedia
          component="img"
          image={signedImageResult.signedUrl}
          alt="Skin condition"
          sx={{ 
            width: '100%',
            height: isMobile ? 200 : '100%',
            objectFit: 'cover'
          }}
        />
      );
    }

    if (signedImageResult?.error) {
      return (
        <Box 
          sx={{ 
            width: '100%',
            height: isMobile ? 200 : '100%',
            bgcolor: 'error.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            p: 1
          }}
        >
          <Typography variant="caption" color="error.main" textAlign="center">
            Failed to load image
          </Typography>
        </Box>
      );
    }

    return (
      <Box 
        sx={{ 
          width: '100%',
          height: isMobile ? 200 : '100%',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No Image
        </Typography>
      </Box>
    );
  };

  const renderContent = () => (
    <CardContent sx={{ 
      width: isMobile ? '100%' : '55%', 
      p: { xs: 2, sm: 2.5 },
      position: 'relative'
    }}>
      {/* Quick Actions - Mobile positioned differently */}
      <Box 
        sx={{ 
          position: 'absolute',
          top: isMobile ? 8 : 16,
          right: isMobile ? 8 : 16,
          display: 'flex',
          gap: 0.5,
          zIndex: 2,
        }}
      >
        <IconButton
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRunInference(sample, e);
          }}
        >
          <Science fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.9)',
            color: 'error.main',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
          }}
          onClick={(e) => onDelete(sample, e)}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ mb: 2, pr: { xs: 6, sm: 8 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
          AI Prediction
        </Typography>
        <Typography 
          variant={isMobile ? "subtitle1" : "h6"}
          component="h3"
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            mb: 1,
            lineHeight: 1.2
          }}
        >
          {sample.prediction || 'No Prediction'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Scanned: {formatSmartDateTime(sample.created_at)}
        </Typography>
      </Box>

      {/* Confidence Bar */}
      {sample.confidence !== undefined && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Confidence
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {(sample.confidence * 100).toFixed(1)}%
            </Typography>
          </Box>
          <Box 
            sx={{ 
              width: '100%',
              height: 6,
              bgcolor: 'grey.200',
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                width: `${sample.confidence * 100}%`,
                height: '100%',
                bgcolor: sample.confidence > 0.7 ? 'success.main' : 
                         sample.confidence > 0.4 ? 'warning.main' : 'error.main',
                transition: 'width 0.3s ease'
              }}
            />
          </Box>
        </Box>
      )}

      {/* Symptoms Description */}
      {sample.symptoms_description && (
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              fontStyle: 'italic',
              display: '-webkit-box',
              WebkitLineClamp: isMobile ? 2 : 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            "{sample.symptoms_description}"
          </Typography>
        </Box>
      )}

      {/* Status Chips */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {/* Show labeled & ready indicator if has disease label and is reviewed */}
        {sample.label_id && (sample.review_status === 'REVIEWED' || sample.review_status === 'APPROVED') && (
          <Chip
            label="Labeled & Ready"
            color="success"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )}
        
        <Chip
          label={sample.review_status}
          color={getStatusColor(sample.review_status)}
          size="small"
        />
        {sample.is_validated && (
          <Chip
            label="Validated"
            color="success"
            variant="outlined"
            size="small"
          />
        )}
        {sample.use_for_training && (
          <Chip
            label="Training"
            color="primary"
            variant="outlined"
            size="small"
          />
        )}
        
        {/* Show disease label if available */}
        {sample.disease && (
          <Chip
            label={`🏷️ ${sample.disease.name}`}
            color="info"
            variant="outlined"
            size="small"
            sx={{ maxWidth: 200 }}
          />
        )}
      </Box>

      {/* Metadata */}
      <Box sx={{ mt: 'auto' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Sample #{sample.id.slice(-8)}
        </Typography>
        {llmAnalysis && (
          <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
            ✓ Staff Diagnosis: {llmAnalysis.diagnosis || llmAnalysis.why}
          </Typography>
        )}
      </Box>
    </CardContent>
  );

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        borderRadius: 3,
        overflow: 'hidden',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.2s ease-in-out',
        height: '100%',
      }}
      onClick={() => onView(sample)}
    >
      {isMobile ? (
        // Mobile: Vertical layout
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ position: 'relative' }}>
            {renderImage()}
          </Box>
          {renderContent()}
        </Box>
      ) : (
        // Desktop: Horizontal layout
        <Box sx={{ display: 'flex', height: 280 }}>
          <Box sx={{ width: '45%', position: 'relative' }}>
            {renderImage()}
          </Box>
          {renderContent()}
        </Box>
      )}
    </Card>
  );
};